import { useState, useEffect } from 'react'
import { useMappedInSpaces, useReactMobileRooms } from 'hooks/useSpaces'
import { Organization } from 'lib/types'
import { compareSpaces, SpaceMatchResult } from 'lib/utils/mappedin'

type Props = {
  onNext: (data: { matchedSpaces: SpaceMatchResult['matchedSpaces'] }) => void
  goBack: () => void
  data: {
    org: Organization | null
    matchedFloors: Record<string, string>
  }
}

export default function Step3MatchSpaces({ onNext, goBack, data }: Props) {
  const [matches, setMatches] = useState<SpaceMatchResult['matchedSpaces']>({})
  const [groupedByFloor, setGroupedByFloor] = useState<SpaceMatchResult['byFloor']>({})
  const [acknowledged, setAcknowledged] = useState(false)

  const miSpacesByFloor = useMappedInSpaces(data.org?.mappedin)
  const rmRoomsByFloor = useReactMobileRooms(data.org?.id || '')

  useEffect(() => {
    if (!miSpacesByFloor || !rmRoomsByFloor || !data.matchedFloors) return

    const result = compareSpaces(miSpacesByFloor, rmRoomsByFloor)
    const { matchedSpaces, byFloor } = result

    setMatches(() => matchedSpaces)
    setGroupedByFloor(() => byFloor)

    console.log('~~Grouped Debug Info:')
    Object.entries(byFloor).forEach(([floor, group]) => {
      group.matched.forEach(({ mi, rm }) => console.log(`✅ MATCHED — ${mi.name} ↔ ${rm} on floor '${floor}'`))
      group.unmatchedMi.forEach(mi => console.log(`❌ ONLY IN Mappedin — ${mi.name} on floor '${floor}'`))
      group.unmatchedRm.forEach(rm => console.log(`❌ ONLY IN ReactMobile — ${rm} on floor '${floor}'`))
    })
  }, [miSpacesByFloor, rmRoomsByFloor, data.matchedFloors])

  const handleSubmit = () => {
    onNext({ matchedSpaces: matches })
  }

  return (
    <div>
      <h2>Step 3: Match Spaces</h2>
      <p>Matches are grouped by floor below:</p>

      {Object.entries(groupedByFloor)
        .sort(([a], [b]) => a.localeCompare(b)) // Sort floor names alphabetically
        .map(([floorName, group]) => {
          const sortedMatched = [...group.matched].sort((a, b) => a.mi.name.localeCompare(b.mi.name))
          const sortedUnmatchedMi = [...group.unmatchedMi].sort((a, b) => a.name.localeCompare(b.name))
          const sortedUnmatchedRm = [...group.unmatchedRm].sort((a, b) => a.localeCompare(b))

          return (
            <div key={floorName} style={{ marginBottom: '2rem' }}>
              <h3>{floorName}</h3>
              <ul>
                {sortedMatched.map(({ mi, rm }) => (
                  <li key={mi.id} style={{ color: 'green' }}>
                    ✅ {mi.name} ↔ {rm}
                  </li>
                ))}
                {sortedUnmatchedMi.map(mi => (
                  <li key={mi.id} style={{ color: 'red' }}>
                    ❌ {mi.name} ↔ (none) — not in React Mobile
                  </li>
                ))}
                {sortedUnmatchedRm.map(rm => (
                  <li key={rm} style={{ color: 'red' }}>
                    ❌ (none) ↔ {rm} — not in Mappedin
                  </li>
                ))}
              </ul>
            </div>
          )
        })}

      <div style={{ marginTop: '1.5rem' }}>
        <label>
          <input
            type="checkbox"
            checked={acknowledged}
            onChange={e => setAcknowledged(e.target.checked)}
            style={{ marginRight: '0.5rem' }}
          />
          Acknowledge you are continuing only with Matching spaces
        </label>
      </div>

      <div style={{ marginTop: '2rem' }}>
        <button
          onClick={goBack}
          style={{
            padding: '0.5rem 1.25rem',
            marginRight: '1rem',
            backgroundColor: '#ddd',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
          }}
        >
          ← Back
        </button>

        <button
          onClick={handleSubmit}
          disabled={!acknowledged}
          style={{
            padding: '0.75rem 1.5rem',
            backgroundColor: acknowledged ? '#29a096' : '#ccc',
            color: 'white',
            fontWeight: 'bold',
            border: 'none',
            borderRadius: '4px',
            cursor: acknowledged ? 'pointer' : 'not-allowed',
          }}
        >
          Continue to Match POIs
        </button>
      </div>
    </div>
  )
}
